"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/Button";
import { ErrorCard } from "@/components/ErrorCard";
import { recoverFromChunkError } from "@/lib/chunkRecovery";
import { reportClientError } from "@/lib/telemetry";

/**
 * Error boundary for the paid-traffic landing pages. Without this the group
 * fell through to the root boundary, which is the worst possible outcome for
 * an ad click: money spent to reach a blank crash screen.
 *
 * The recovery actions deliberately differ from (site)'s "Back home" — a
 * visitor who arrived from an ad has an intent already, so the offer is to
 * retry or go straight to the thing the ad promised, not to the homepage.
 */
export default function LandingError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  // A chunk that vanished under a mid-visit deploy is repairable, and the
  // card below can't repair it — reload instead. Decided during the first
  // render (via the lazy initializer) so the crash screen never flashes up
  // on its way out; the sessionStorage guard inside makes the repeat call
  // under StrictMode's double render a no-op.
  const [recovering] = useState(() => recoverFromChunkError(error));

  useEffect(() => {
    console.error(error);
    reportClientError(error, "landing", { recovered: String(recovering) });
  }, [error, recovering]);

  // The reload is already scheduled; showing anything would only flicker.
  if (recovering) return null;

  return (
    <div className="mx-auto max-w-[560px] px-[var(--gutter)] py-20">
      <ErrorCard
        title="This page didn't load"
        body="Something broke on our side — not on yours. Try again, or jump straight to starting a rule book."
        digest={error.digest}
        action={
          <>
            <Button onClick={() => unstable_retry()}>Try again</Button>
            <Button variant="secondary" href="/create">
              Start a rule book
            </Button>
          </>
        }
      />
    </div>
  );
}
